import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  query,
  orderBy,
  limit,
  addDoc,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import axios from 'axios';
import TimeDatePickerModal from '../common/TimeDatePickerModal';
import './Chat.css';

// Determine API base URL (same as Dashboard)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
}

const API_BASE_URL = getApiBaseUrl();

const Chat = forwardRef((props, ref) => {
  const { currentUser, currentRole } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const {
    initialConversationId,
    onBackToList,
    onOpenChat,
    showChatView,
    setShowChatView
  } = props;
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversationId, setConversationId] = useState(initialConversationId || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationStatus, setConversationStatus] = useState(null);
  const [isInitiator, setIsInitiator] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [completeFreelancer, setCompleteFreelancer] = useState(false);
  const [completeBusiness, setCompleteBusiness] = useState(false);
  const [workProposal, setWorkProposal] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposedStartDate, setProposedStartDate] = useState('');
  const [proposedStartTime, setProposedStartTime] = useState('');
  const [proposedEndDate, setProposedEndDate] = useState('');
  const [proposedEndTime, setProposedEndTime] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const processedMessagesRef = useRef(new Set()); // Track processed messages for receipts
  const messageInputRef = useRef(null);
  const intentionallyClosedRef = useRef(false); // Track if user intentionally closed conversation

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const loadConversations = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/api/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const conversations = response.data.conversations || [];
      // Sort conversations: first by job (with jobs first), then by updatedAt
      conversations.sort((a, b) => {
        const aHasJob = !!(a.job || a.jobId);
        const bHasJob = !!(b.job || b.jobId);

        // Conversations with jobs come first
        if (aHasJob && !bHasJob) return -1;
        if (!aHasJob && bHasJob) return 1;

        // If both have jobs, sort by job title, then by updatedAt
        if (aHasJob && bHasJob) {
          const aJobTitle = a.job?.title || a.jobId || '';
          const bJobTitle = b.job?.title || b.jobId || '';
          if (aJobTitle !== bJobTitle) {
            return aJobTitle.localeCompare(bJobTitle);
          }
        }

        // Then sort by updatedAt (most recent first)
        const aUpdated = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt || 0);
        const bUpdated = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt || 0);
        return bUpdated - aUpdated;
      });

      setConversations(conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    }
  }, [currentUser]);

  const handleOpenConversation = useCallback((conversation) => {
    intentionallyClosedRef.current = false; // Reset closed flag when opening
    setSelectedUser(conversation.otherUser);
    setConversationId(conversation.conversationId);
    // Preserve status including 'completed' - don't default to 'accepted'
    setConversationStatus(conversation.status || 'accepted');
    setIsInitiator(conversation.isInitiator || false);
    setCompleteFreelancer(!!conversation.completeFreelancer);
    setCompleteBusiness(!!conversation.completeBusiness);
    setWorkProposal(conversation.workProposal || null);
    setJobData(conversation.job || null);
    // Switch to chat view on mobile
    if (setShowChatView) {
      setShowChatView(true);
    }
    if (onOpenChat) {
      onOpenChat();
    }
  }, [setShowChatView, onOpenChat]);

  const handleCloseConversation = useCallback(() => {
    // Mark as intentionally closed
    intentionallyClosedRef.current = true;

    // Clean up Firestore listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Clear processed messages
    processedMessagesRef.current.clear();

    // Reset all state
    setSelectedUser(null);
    setConversationId(null);
    setConversationStatus(null);
    setIsInitiator(false);
    setMessages([]);
    setNewMessage('');
    setJobData(null);

    // Switch back to list view on mobile
    if (setShowChatView) {
      setShowChatView(false);
    }
    if (onBackToList) {
      onBackToList();
    }
  }, [setShowChatView, onBackToList]);

  // Load conversations on mount and set up polling for real-time updates
  useEffect(() => {
    if (!currentUser) return;

    // Initial load
    loadConversations();

    // Set up polling to refresh conversations periodically (every 3 seconds)
    // This ensures new conversations appear when invites are accepted/received
    const pollInterval = setInterval(() => {
      loadConversations();
    }, 3000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [currentUser, loadConversations]);

  // Open conversation from initialConversationId prop when conversations are loaded
  useEffect(() => {
    if (initialConversationId && conversations.length > 0 && !selectedUser && !intentionallyClosedRef.current) {
      const conversation = conversations.find(c => c.conversationId === initialConversationId);
      if (conversation) {
        handleOpenConversation(conversation);
        // Switch to chat view when opening from URL
        if (setShowChatView) {
          setShowChatView(true);
        }
      }
    }
  }, [initialConversationId, conversations, selectedUser, handleOpenConversation, setShowChatView]);

  // Update conversation status and job data when conversations list is refreshed
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const currentConversation = conversations.find(c => c.conversationId === conversationId);
      if (currentConversation) {
        // Update status if it changed
        setConversationStatus(prevStatus => {
          const newStatus = currentConversation.status || 'accepted';
          return newStatus !== prevStatus ? newStatus : prevStatus;
        });
        // Update isInitiator if it changed
        setIsInitiator(prevIsInitiator => {
          const newIsInitiator = currentConversation.isInitiator || false;
          return newIsInitiator !== prevIsInitiator ? newIsInitiator : prevIsInitiator;
        });
        setCompleteFreelancer(!!currentConversation.completeFreelancer);
        setCompleteBusiness(!!currentConversation.completeBusiness);
        setWorkProposal(currentConversation.workProposal || null);
        // Update job data if it exists or changed (for new job assignments in same conversation)
        if (currentConversation.job) {
          setJobData(currentConversation.job);
        }
      }
    }
  }, [conversations, conversationId]);

  // Hide/reset proposal form if conversation changes, gets confirmed, or if work proposal already exists from job
  useEffect(() => {
    if (!conversationId || (workProposal && workProposal.status === 'confirmed')) {
      setShowProposalForm(false);
    }
    // If work proposal already exists (from job auto-creation), don't show proposal form
    if (workProposal && workProposal.status && workProposal.status !== 'rejected') {
      setShowProposalForm(false);
    }
  }, [conversationId, workProposal]);

  // Load messages when conversation changes
  useEffect(() => {
    // Clear processed messages when conversation changes
    processedMessagesRef.current.clear();

    if (conversationId && currentUser) {
      loadMessages();
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } else {
      setMessages([]);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    }
  }, [conversationId, currentUser]);

  // Mark messages as delivered and read when conversation is open and messages change
  useEffect(() => {
    if (conversationId && currentUser && selectedUser && messages.length > 0) {
      // Debounce to avoid too many updates
      const timeoutId = setTimeout(() => {
        markMessagesAsDelivered();
        markMessagesAsRead();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, conversationId, currentUser, selectedUser]);

  // Auto-resize message input as user types
  useEffect(() => {
    if (messageInputRef.current) {
      const el = messageInputRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [newMessage]);

  // Keep the message input focused when opening a chat and when allowed to type
  useEffect(() => {
    if (selectedUser && messageInputRef.current && (conversationStatus === 'accepted' || conversationStatus === 'completed')) {
      // Focus immediately and also in next tick to handle iOS quirks
      messageInputRef.current.focus();
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 0);
    }
  }, [selectedUser, conversationStatus, showChatView]);


  const loadMessages = () => {
    if (!conversationId) return;

    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');

    // Try to query with orderBy first (requires index)
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const loadedMessages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort messages by timestamp client-side as well (for consistency).
        // Place messages without a resolved server timestamp at the end using clientCreatedAt.
        loadedMessages.sort((a, b) => {
          const getMs = (m) => {
            if (m?.timestamp?.toDate) return m.timestamp.toDate().getTime();
            if (m?.timestamp instanceof Date) return m.timestamp.getTime();
            if (typeof m?.timestamp === 'number') return m.timestamp;
            if (typeof m?.clientCreatedAt === 'number') return m.clientCreatedAt;
            if (typeof m?.createdAt === 'number') return m.createdAt;
            return Number.MAX_SAFE_INTEGER; // push unknowns to bottom
          };
          return getMs(a) - getMs(b);
        });
        setMessages(loadedMessages);
      },
      (error) => {
        console.error('Error loading messages with orderBy:', error);

        // Fallback: try without orderBy if index is missing
        if (error.code === 'failed-precondition' || error.code === 'unavailable' || error.message?.includes('index')) {
          console.log('Attempting fallback query without orderBy...');
          const fallbackQ = query(messagesRef, limit(100));

          // Clean up the failed listener
          if (unsubscribeRef.current) {
            unsubscribeRef.current();
          }

          unsubscribeRef.current = onSnapshot(fallbackQ,
            (snapshot) => {
              const loadedMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              // Sort messages by timestamp client-side (same logic as above)
              loadedMessages.sort((a, b) => {
                const getMs = (m) => {
                  if (m?.timestamp?.toDate) return m.timestamp.toDate().getTime();
                  if (m?.timestamp instanceof Date) return m.timestamp.getTime();
                  if (typeof m?.timestamp === 'number') return m.timestamp;
                  if (typeof m?.clientCreatedAt === 'number') return m.clientCreatedAt;
                  if (typeof m?.createdAt === 'number') return m.createdAt;
                  return Number.MAX_SAFE_INTEGER;
                };
                return getMs(a) - getMs(b);
              });
              setMessages(loadedMessages);
            },
            (fallbackError) => {
              console.error('Error loading messages (fallback):', fallbackError);
            }
          );
        } else {
          console.error('Unexpected error loading messages:', error);
        }
      }
    );

    unsubscribeRef.current = unsubscribe;
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/api/chat/search-users`, {
        params: { email: searchEmail },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = useCallback(async (user) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/api/chat/conversation`, {
        otherUserId: user.uid
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSelectedUser(response.data.otherUser);
      setConversationId(response.data.conversationId);
      setConversationStatus(response.data.status);
      setIsInitiator(response.data.isInitiator);
      setSearchEmail('');
      setSearchResults([]);

      // Reload conversations to update the list
      loadConversations();
      // Trigger a custom event to notify Dashboard to refresh UserCards
      window.dispatchEvent(new CustomEvent('conversationCreated'));

      // Switch to chat view on mobile
      if (setShowChatView) {
        setShowChatView(true);
      }
      if (onOpenChat) {
        onOpenChat();
      }
    } catch (error) {
      console.error('Error selecting user:', error);
    }
  }, [currentUser, loadConversations, setShowChatView, onOpenChat]);

  // Expose method to open conversation with a user (for UserCards component)
  useImperativeHandle(ref, () => ({
    openConversationWithUser: handleSelectUser
  }), [handleSelectUser]);

  const handleSendMessage = async () => {
    // Prevent sending messages if conversation is not accepted or completed
    // Completed conversations should still allow messaging for future jobs
    if (conversationStatus !== 'accepted' && conversationStatus !== 'completed') {
      alert(t('chat.waitForAcceptance'));
      return;
    }

    if (!newMessage.trim() || !conversationId || sending) return;

    setSending(true);
    try {
      const messageData = {
        text: newMessage.trim(),
        senderId: currentUser.uid,
        senderEmail: currentUser.email,
        timestamp: serverTimestamp(),
        clientCreatedAt: Date.now(),
        delivered: false,
        read: false,
        deliveredAt: null,
        readAt: null
      };

      // Add message to Firestore
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      await addDoc(messagesRef, messageData);

      // Update conversation's updatedAt timestamp
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        updatedAt: serverTimestamp()
      });

      setNewMessage('');
      // Keep keyboard open by refocusing the textarea
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
      // Also focus on next tick for mobile Safari reliability
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 0);
      // Reload conversations to update last message
      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Mark messages as delivered when recipient loads them
  const markMessagesAsDelivered = async (messagesToProcess = messages) => {
    if (!conversationId || !currentUser || !selectedUser) return;

    try {
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const updates = [];

      for (const message of messagesToProcess) {
        // Only mark as delivered if:
        // 1. Message is not from current user
        // 2. Message hasn't been delivered yet
        // 3. Message hasn't been processed before
        if (
          message.senderId !== currentUser.uid &&
          !message.delivered &&
          !processedMessagesRef.current.has(message.id)
        ) {
          processedMessagesRef.current.add(message.id);
          const messageRef = doc(messagesRef, message.id);
          updates.push(
            updateDoc(messageRef, {
              delivered: true,
              deliveredAt: serverTimestamp()
            })
          );
        }
      }

      if (updates.length > 0) {
        await Promise.all(updates);
      }
    } catch (error) {
      console.error('Error marking messages as delivered:', error);
    }
  };

  // Mark messages as read when recipient views them (when conversation is open)
  const markMessagesAsRead = async () => {
    if (!conversationId || !currentUser || !selectedUser) return;

    try {
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const updates = [];

      for (const message of messages) {
        // Only mark as read if:
        // 1. Message is not from current user
        // 2. Message has been delivered
        // 3. Message hasn't been read yet
        if (
          message.senderId !== currentUser.uid &&
          message.delivered &&
          !message.read
        ) {
          const messageRef = doc(messagesRef, message.id);
          updates.push(
            updateDoc(messageRef, {
              read: true,
              readAt: serverTimestamp()
            })
          );
        }
      }

      if (updates.length > 0) {
        await Promise.all(updates);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAcceptConversation = async () => {
    if (!conversationId) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/accept`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setConversationStatus('accepted');
      await loadConversations();
      // Trigger a custom event to notify Dashboard to refresh UserCards
      window.dispatchEvent(new CustomEvent('conversationAccepted'));
    } catch (error) {
      console.error('Error accepting conversation:', error);
      alert(t('chat.failedAccept'));
    }
  };

  const handleRejectConversation = async () => {
    if (!conversationId) return;

    if (!window.confirm(t('chat.sureReject'))) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/reject`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Close the conversation view
      handleCloseConversation();
      await loadConversations();
      // Trigger a custom event to notify Dashboard to refresh UserCards
      window.dispatchEvent(new CustomEvent('conversationRejected'));
    } catch (error) {
      console.error('Error rejecting conversation:', error);
      alert(t('chat.failedReject'));
    }
  };

  const handleCancelConversation = async () => {
    if (!conversationId) return;

    if (!window.confirm(t('chat.sureCancel'))) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Close the conversation view
      handleCloseConversation();
      loadConversations();
    } catch (error) {
      console.error('Error cancelling conversation:', error);
      alert(t('chat.failedCancel'));
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId) return;

    if (!window.confirm(t('chat.sureDelete'))) {
      return;
    }

    setDeletingConversation(true);
    try {
      const token = await currentUser.getIdToken();
      await axios.delete(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Close the conversation view
      handleCloseConversation();
      loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert(t('chat.failedDelete'));
    } finally {
      setDeletingConversation(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    // Convert to Israel timezone for consistent display
    const toIsraelDate = (d) => {
      const tzString = d.toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' });
      return new Date(tzString);
    };
    const israelNow = toIsraelDate(new Date());
    const israelDate = toIsraelDate(date);

    const todayIL = new Date(israelNow.getFullYear(), israelNow.getMonth(), israelNow.getDate());
    const messageDayIL = new Date(israelDate.getFullYear(), israelDate.getMonth(), israelDate.getDate());

    const locale = isRTL ? 'he-IL' : 'en-US';
    const timeStr = israelDate.toLocaleTimeString(locale, {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jerusalem'
    });

    if (messageDayIL.getTime() === todayIL.getTime()) {
      // Today - show time only
      return timeStr;
    } else if (messageDayIL.getTime() === todayIL.getTime() - 86400000) {
      // Yesterday
      return t('chat.yesterday') + ' ' + timeStr;
    } else {
      // Older - dd-mm-yyyy HH:MM
      const dd = String(israelDate.getDate()).padStart(2, '0');
      const mm = String(israelDate.getMonth() + 1).padStart(2, '0');
      const yyyy = israelDate.getFullYear();
      return `${dd}-${mm}-${yyyy} ${timeStr}`;
    }
  };

  const handleProposeSchedule = () => {
    // Open inline proposal form
    setShowProposalForm(true);
  };

  const submitScheduleProposal = async () => {
    if (!conversationId) return;
    // Basic validation similar to business job form
    if (!proposedStartDate) {
      alert(t('dashboard.jobStartDateRequired'));
      return;
    }
    if (!proposedStartTime) {
      alert(t('dashboard.jobStartTimeRequired'));
      return;
    }
    if (!proposedEndDate) {
      alert(t('dashboard.jobEndDateRequired'));
      return;
    }
    if (!proposedEndTime) {
      alert(t('dashboard.jobEndTimeRequired'));
      return;
    }
    // Ensure end is not before start in local comparison
    const startKey = `${proposedStartDate}T${proposedStartTime}`;
    const endKey = `${proposedEndDate}T${proposedEndTime}`;
    if (endKey < startKey) {
      if (proposedEndDate < proposedStartDate) {
        alert(t('dashboard.jobEndDateBeforeStart'));
      } else {
        alert(t('dashboard.jobEndTimeBeforeStart'));
      }
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      const payload = {
        slots: [{
          startDate: proposedStartDate,
          startTime: proposedStartTime,
          endDate: proposedEndDate,
          endTime: proposedEndTime
        }]
      };
      const res = await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/propose-schedule`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = res.data || {};
      setWorkProposal(data.workProposal || { status: 'proposed', ...payload });
      loadConversations();
      setShowProposalForm(false);
      alert(t('chat.scheduleSubmitted') || 'Schedule proposed');
    } catch (e) {
      console.error('Failed to propose schedule', e);
      alert(t('chat.failedProposeSchedule') || 'Failed to propose schedule');
    }
  };

  const handleConfirmSchedule = async () => {
    if (!conversationId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/confirm-schedule`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = res.data || {};
      setWorkProposal(prev => ({ ...(prev || {}), ...(data.workProposal || {}), status: 'confirmed' }));
      loadConversations();
      alert(t('chat.scheduleConfirmedToast') || 'Schedule confirmed');
    } catch (e) {
      console.error('Failed to confirm schedule', e);
      alert(t('chat.failedConfirmSchedule') || 'Failed to confirm schedule');
    }
  };

  const handleRejectSchedule = async () => {
    if (!conversationId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await axios.post(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/reject-schedule`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = res.data || {};
      setWorkProposal(prev => ({ ...(prev || {}), ...(data.workProposal || {}), status: 'rejected' }));
      loadConversations();
      alert(t('chat.scheduleRejectedToast') || 'Schedule rejected');
    } catch (e) {
      console.error('Failed to reject schedule', e);
      alert(t('chat.failedRejectSchedule') || 'Failed to reject schedule');
    }
  };

  const renderScheduleSummary = () => {
    if (!workProposal || !workProposal.status || !workProposal.slots || workProposal.slots.length === 0) {
      return null;
    }
    const slot = workProposal.slots[0];
    const statusLabel = workProposal.status === 'confirmed'
      ? (t('chat.scheduleConfirmed') || 'Schedule confirmed')
      : workProposal.status === 'proposed'
      ? (t('chat.schedulePending') || 'Schedule proposed, awaiting confirmation')
      : (t('chat.scheduleRejected') || 'Schedule rejected');
    return (
      <div className="chat-schedule-summary">
        <div className="chat-schedule-status">{statusLabel}</div>
        <div className="chat-schedule-slot">
          {slot.startDate} {slot.startTime} → {slot.endDate} {slot.endTime}
        </div>
      </div>
    );
  };

  return (
    <div className={`chat-container ${isRTL ? 'rtl' : ''} ${showChatView ? 'show-chat-view' : 'show-list-view'}`}>
      <div className={`chat-sidebar ${showChatView ? 'hidden-mobile' : ''}`}>
        <div className="chat-header">
          <h3>{t('chat.title')}</h3>
        </div>

        {/* User Search */}
        <div className="user-search">
          <div className="search-input-container">
            <input
              type="text"
              placeholder={t('chat.searchUserByEmail')}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className="search-input"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="search-button"
            >
              {searching ? '...' : t('chat.search')}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <div className="search-results-header">{t('chat.searchResults')}</div>
              {searchResults.map((user) => (
                <div
                  key={user.uid}
                  className="search-result-item"
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-avatar">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{user.displayName || (() => {
                      const emailPart = user.email.split('@')[0];
                      return emailPart.replace(/@gmail\.com$/i, '').replace(/@.*$/, '');
                    })()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conversations List - Grouped by Job */}
        <div className="conversations-list">
          <div className="conversations-header">{t('chat.conversations')}</div>
          {conversations.length === 0 ? (
            <div className="no-conversations">
              {t('chat.noConversations')}
            </div>
          ) : (
            (() => {
              // Group conversations by job
              const conversationsByJob = {};
              const conversationsWithoutJob = [];

              conversations.forEach((conversation) => {
                const jobId = conversation.job?.id || conversation.jobId;
                const jobTitle = conversation.job?.title || conversation.jobId || null;

                // Check if job is completed
                const isCompleted = conversation.status === 'completed' ||
                  (conversation.completeFreelancer && conversation.completeBusiness);

                // Only group by job if job exists and is NOT completed
                // Completed jobs are ungrouped but chats remain open
                if (jobId && jobTitle && !isCompleted) {
                  if (!conversationsByJob[jobId]) {
                    conversationsByJob[jobId] = {
                      jobId,
                      jobTitle,
                      job: conversation.job,
                      conversations: []
                    };
                  }
                  conversationsByJob[jobId].conversations.push(conversation);
                } else {
                  // Include conversations without jobs OR with completed jobs
                  conversationsWithoutJob.push(conversation);
                }
              });

              // Sort conversations within each job group by updatedAt
              Object.values(conversationsByJob).forEach((jobGroup) => {
                jobGroup.conversations.sort((a, b) => {
                  const aUpdated = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt || 0);
                  const bUpdated = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt || 0);
                  return bUpdated - aUpdated;
                });
              });

              // Sort job groups by most recent conversation
              const jobGroups = Object.values(conversationsByJob).sort((a, b) => {
                const aLatest = a.conversations[0]?.updatedAt?.toDate
                  ? a.conversations[0].updatedAt.toDate().getTime()
                  : (a.conversations[0]?.updatedAt || 0);
                const bLatest = b.conversations[0]?.updatedAt?.toDate
                  ? b.conversations[0].updatedAt.toDate().getTime()
                  : (b.conversations[0]?.updatedAt || 0);
                return bLatest - aLatest;
              });

              // Sort ungrouped conversations (including completed jobs) by updatedAt
              conversationsWithoutJob.sort((a, b) => {
                const aUpdated = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt || 0);
                const bUpdated = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt || 0);
                return bUpdated - aUpdated;
              });

              return (
                <>
                  {/* Conversations grouped by job */}
                  {jobGroups.map((jobGroup) => (
                    <div key={jobGroup.jobId} className="job-group">
                      <div className="job-group-header">
                        <div className="job-group-title">
                          <span className="job-group-icon">📋</span>
                          <span className="job-group-name">{jobGroup.jobTitle}</span>
                        </div>
                        {jobGroup.job && (
                          <div className="job-group-meta">
                            {jobGroup.job.start_date && (
                              <span className="job-group-date">
                                📅 {jobGroup.job.start_date}
                                {jobGroup.job.end_date && jobGroup.job.end_date !== jobGroup.job.start_date && ` - ${jobGroup.job.end_date}`}
                              </span>
                            )}
                            {jobGroup.job.location && (
                              <span className="job-group-location">📍 {jobGroup.job.location}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="job-group-conversations">
                        {jobGroup.conversations.map((conversation) => (
                          <div
                            key={conversation.conversationId}
                            className={`conversation-item ${
                              conversation.conversationId === conversationId ? 'active' : ''
                            }`}
                            onClick={() => handleOpenConversation(conversation)}
                          >
                            <div className="user-avatar">
                              {conversation.otherUser.email.charAt(0).toUpperCase()}
                            </div>
                            <div className="conversation-info">
                              <div className="conversation-name">
                                {currentRole === 'freelancer'
                                  ? (conversation.otherUser.displayName || conversation.otherUser.email.split('@')[0])
                                  : (conversation.otherUser.displayName || conversation.otherUser.email.split('@')[0])}
                                {conversation.status === 'pending' && (
                                  <span className="conversation-status-badge pending">{t('chat.pending')}</span>
                                )}
                                {conversation.workProposal?.status === 'confirmed' && (
                                  <span className="conversation-status-badge confirmed" title={t('chat.scheduleConfirmed')}>✓</span>
                                )}
                                {conversation.workProposal?.status === 'proposed' && (
                                  <span className="conversation-status-badge proposed" title={t('chat.schedulePending')}>⏱</span>
                                )}
                              </div>
                              {conversation.status === 'pending' ? (
                                <div className="conversation-last-message">
                                  {conversation.isInitiator ? t('chat.waitingForAcceptance') : t('chat.newConversationRequest')}
                                </div>
                              ) : conversation.lastMessage ? (
                                <div className="conversation-last-message">
                                  {conversation.lastMessage.text || '(Media)'}
                                </div>
                              ) : (
                                <div className="conversation-last-message">
                                  {t('chat.noMessages')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Conversations without jobs */}
                  {conversationsWithoutJob.length > 0 && (
                    <div className="job-group">
                      <div className="job-group-header">
                        <div className="job-group-title">
                          <span className="job-group-icon">💬</span>
                          <span className="job-group-name">{t('chat.generalConversations') || 'General Conversations'}</span>
                        </div>
                      </div>
                      <div className="job-group-conversations">
                        {conversationsWithoutJob.map((conversation) => {
                          // Check if this is a completed job conversation
                          const isCompletedJob = conversation.status === 'completed' ||
                            (conversation.completeFreelancer && conversation.completeBusiness);
                          const hasJob = !!(conversation.job || conversation.jobId);

                          return (
                            <div
                              key={conversation.conversationId}
                              className={`conversation-item ${
                                conversation.conversationId === conversationId ? 'active' : ''
                              }`}
                              onClick={() => handleOpenConversation(conversation)}
                            >
                              <div className="user-avatar">
                                {conversation.otherUser.email.charAt(0).toUpperCase()}
                              </div>
                              <div className="conversation-info">
                                <div className="conversation-name">
                                  {conversation.otherUser.displayName || conversation.otherUser.email.split('@')[0]}
                                  {conversation.status === 'pending' && (
                                    <span className="conversation-status-badge pending">{t('chat.pending')}</span>
                                  )}
                                  {isCompletedJob && hasJob && (
                                    <span className="conversation-status-badge completed" title={t('jobPage.phaseCompleted') || 'Job completed'}>✓</span>
                                  )}
                                </div>
                                {isCompletedJob && hasJob && conversation.job && (
                                  <div className="conversation-job-title" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                                    📋 {conversation.job.title || t('jobs.jobTitle')} ({t('jobPage.phaseCompleted') || 'Completed'})
                                  </div>
                                )}
                                {conversation.status === 'pending' ? (
                                  <div className="conversation-last-message">
                                    {conversation.isInitiator ? t('chat.waitingForAcceptance') : t('chat.newConversationRequest')}
                                  </div>
                                ) : conversation.lastMessage ? (
                                  <div className="conversation-last-message">
                                    {conversation.lastMessage.text || '(Media)'}
                                  </div>
                                ) : (
                                  <div className="conversation-last-message">
                                    {t('chat.noMessages')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      </div>

      <div className={`chat-main ${showChatView ? 'visible-mobile' : 'hidden-mobile'}`}>
        {selectedUser ? (
          <>
            <div className="chat-header-main">
              <div className="chat-header-left">
                <button
                  className="chat-back-button-mobile"
                  onClick={handleCloseConversation}
                  aria-label={t('common.back')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </button>
                <div className="chat-user-info">
                  <div className="user-avatar-large">
                    {selectedUser.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="chat-user-name">
                      {selectedUser.displayName || (() => {
                        const emailPart = selectedUser.email.split('@')[0];
                        return emailPart.replace(/@gmail\.com$/i, '').replace(/@.*$/, '');
                      })()}
                      {conversationStatus === 'pending' && (
                        <span className="conversation-status-badge pending">{t('chat.pending')}</span>
                      )}
                    </div>
                    {jobData && (
                      <div className="chat-job-info">
                        <div className="chat-job-header">
                          <div className="chat-job-title">📋 {jobData.title || t('jobs.jobTitle')}</div>
                          {conversationId && (
                            <button
                              type="button"
                              className="chat-job-view-btn"
                              onClick={() => navigate(`/job/${conversationId}`)}
                              title={t('jobPage.viewJob') || 'View job page'}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                              </svg>
                              <span>{t('jobPage.viewJob') || 'View Job'}</span>
                            </button>
                          )}
                        </div>
                        <div className="chat-job-details">
                          {jobData.start_date && (
                            <div className="chat-job-date">
                              📅 {jobData.start_date}
                              {jobData.end_date && jobData.end_date !== jobData.start_date && ` - ${jobData.end_date}`}
                              {jobData.start_time && (
                                <span> {jobData.start_time}
                                  {jobData.end_time && ` - ${jobData.end_time}`}
                                </span>
                              )}
                            </div>
                          )}
                          {jobData.location && (
                            <div className="chat-job-location">📍 {jobData.location}</div>
                          )}
                          {(jobData.hourly_rate || jobData.day_rate) && (
                            <div className="chat-job-rate">
                              💵 {jobData.hourly_rate ? `₪${jobData.hourly_rate}/hr` : ''}
                              {jobData.hourly_rate && jobData.day_rate ? ' / ' : ''}
                              {jobData.day_rate ? `₪${jobData.day_rate}/day` : ''}
                            </div>
                          )}
                          {jobData.category && (
                            <div className="chat-job-category">
                              🏷️ {jobData.category}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {renderScheduleSummary()}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                {conversationStatus === 'pending' && !isInitiator && (
                  <>
                    <button
                      className="accept-conversation-btn"
                      onClick={handleAcceptConversation}
                    >
                      {t('chat.accept')}
                    </button>
                    <button
                      className="reject-conversation-btn"
                      onClick={handleRejectConversation}
                    >
                      {t('chat.reject')}
                    </button>
                  </>
                )}
                {conversationStatus === 'pending' && isInitiator && (
                  <button
                    className="cancel-conversation-btn"
                    onClick={handleCancelConversation}
                  >
                    {t('chat.cancelRequest')}
                  </button>
                )}
                {(currentRole === 'business' || currentRole === 'employer') && selectedUser?.uid && (
                  <button
                    className="propose-schedule-btn"
                    type="button"
                    onClick={() => navigate(`/freelancer/${selectedUser.uid}/public`)}
                  >
                    {t('jobs.viewProfile') || 'View freelancer'}
                  </button>
                )}
                {currentRole === 'freelancer' && selectedUser?.uid && (
                  <button
                    className="propose-schedule-btn"
                    type="button"
                    onClick={() => navigate(`/business/${selectedUser.uid}/public`)}
                  >
                    {t('jobs.viewBusiness') || 'View business'}
                  </button>
                )}
                {(conversationStatus === 'accepted' || conversationStatus === 'completed') && (
                  <>
                    {currentRole === 'freelancer' && (!workProposal || workProposal.status !== 'confirmed') && (
                      <>
                        {!showProposalForm ? (
                          <button
                            className="propose-schedule-btn"
                            onClick={handleProposeSchedule}
                          >
                            {t('chat.proposeSchedule') || 'Propose Schedule'}
                          </button>
                        ) : (
                          <div className="proposal-form" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => setShowScheduleModal(true)}
                              className="propose-schedule-btn"
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: 'rgba(76, 175, 80, 0.2)',
                                border: '1px solid rgba(76, 175, 80, 0.4)',
                                borderRadius: '6px',
                                color: 'rgba(255, 255, 255, 0.9)',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                            >
                              {proposedStartDate && proposedEndDate
                                ? `${proposedStartDate} ${proposedStartTime || ''} - ${proposedEndDate} ${proposedEndTime || ''}`
                                : t('chat.selectSchedule') || 'Select Schedule'}
                            </button>
                            {(proposedStartDate || proposedEndDate) && (
                              <button
                                className="confirm-schedule-btn"
                                onClick={submitScheduleProposal}
                              >
                                {t('common.save')}
                              </button>
                            )}
                            <button
                              className="reject-schedule-btn"
                              onClick={() => {
                                setShowProposalForm(false);
                                setProposedStartDate('');
                                setProposedStartTime('');
                                setProposedEndDate('');
                                setProposedEndTime('');
                              }}
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                {/* Delete button always visible */}
                {selectedUser && (
                  <button
                    className="delete-conversation-btn"
                    onClick={handleDeleteConversation}
                    disabled={deletingConversation}
                  >
                    {deletingConversation ? t('chat.deleting') : t('common.delete')}
                  </button>
                )}
                <button
                  className="close-chat-btn desktop-only"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCloseConversation();
                  }}
                  type="button"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {conversationStatus === 'pending' && isInitiator ? (
                <div className="conversation-pending-notice">
                  <p>{t('chat.pendingNoticeInitiator', { user: selectedUser.displayName || selectedUser.email.split('@')[0] })}</p>
                </div>
              ) : conversationStatus === 'pending' && !isInitiator ? (
                <div className="conversation-pending-notice">
                  <p>{t('chat.pendingNoticeRecipient', { user: selectedUser.displayName || selectedUser.email.split('@')[0] })}</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  {t('chat.noMessages')}
                </div>
              ) : (
                messages.map((message) => {
                  const isOwnMessage = message.senderId === currentUser.uid;
                  return (
                    <div
                      key={message.id}
                      className={`message ${isOwnMessage ? 'own-message' : 'other-message'}`}
                    >
                      <div className="message-content">
                        <div className="message-text">{message.text}</div>
                        <div className="message-footer">
                          <div className="message-timestamp">
                            {formatTimestamp(message.timestamp)}
                          </div>
                          {isOwnMessage && (
                            <div className="message-receipts">
                              {message.read ? (
                                <span className="double-checkmark-icon" title={t('chat.read')}>
                                  ✓✓
                                </span>
                              ) : message.delivered ? (
                                <span className="single-checkmark-icon" title={t('chat.delivered')}>
                                  ✓
                                </span>
                              ) : (
                                <span className="pending-icon" title={t('chat.sending')}>
                                  ○
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-container">
              <textarea
                ref={messageInputRef}
                rows={1}
                placeholder={(conversationStatus === 'accepted' || conversationStatus === 'completed') ? t('chat.typeMessage') : t('chat.conversationNotAccepted')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                className="message-input"
                disabled={sending || (conversationStatus !== 'accepted' && conversationStatus !== 'completed')}
                autoFocus={conversationStatus === 'accepted' || conversationStatus === 'completed'}
              />
              <button
                onMouseDown={(e) => {
                  // Prevent the button from stealing focus so the keyboard stays open
                  e.preventDefault();
                  if (messageInputRef.current) {
                    messageInputRef.current.focus();
                  }
                }}
                onTouchStart={(e) => {
                  // Same for touch devices (iOS Safari)
                  e.preventDefault();
                  if (messageInputRef.current) {
                    messageInputRef.current.focus();
                  }
                }}
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending || (conversationStatus !== 'accepted' && conversationStatus !== 'completed')}
                className="send-button"
                aria-label={t('chat.send')}
              >
                {sending ? (
                  <span className="send-button-loading">...</span>
                ) : (
                  <>
                    <span className="send-button-icon">➤</span>
                    <span className="send-button-text">{t('chat.send')}</span>
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="chat-placeholder">
            <div className="placeholder-content">
              <h3>{t('chat.selectConversation')}</h3>
              <p>{t('chat.selectConversationHint')}</p>
            </div>
          </div>
        )}
      </div>

      <TimeDatePickerModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onConfirm={(result) => {
          setProposedStartDate(result.date || '');
          setProposedStartTime(result.time || '');
          setProposedEndDate(result.endDate || '');
          setProposedEndTime(result.endTime || '');
        }}
        mode="datetimerange"
        initialDate={proposedStartDate}
        initialTime={proposedStartTime}
        initialEndDate={proposedEndDate}
        initialEndTime={proposedEndTime}
        title={t('chat.selectSchedule') || 'Select Schedule'}
      />
    </div>
  );
});

Chat.displayName = 'Chat';

export default Chat;

